import type { MutableRefObject } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { useBuildURL } from '../../../hooks/useBuildURL';
import { useScrollListener } from '../../../hooks/useScrollListener';
import { Text } from '../../../ui-components';
import { BasketIcon, HamburgerIcon, SearchIcon } from '../../icons';
import { SoftLink } from '../../soft-link/SoftLink';
import styles from './Menu.module.scss';
import { HeaderLogoLink } from './header-logo-link';
import { MenuCategory } from './menu-category';
import { MenuCustomer } from './menu-customer';
import { MenuSearch } from './menu-search';
import { MenuStore } from './menu-store';

interface Props {
    headerRef: MutableRefObject<HTMLElement>;
}

export const Menu: React.FC<Props> = ({ headerRef }: Props) => {
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { data } = useBasket();
    const { buildCartURL } = useBuildURL();
    const { isScrolled, isNotScrolled } = useScrollListener();
    const isMobile = useIsMobile();

    const cartUrl = buildCartURL();

    const basketCount =
        data?.productItems?.reduce((acc, item) => acc + (item.quantity ?? 1), 0) ?? 0;

    const showSearchInput = !isMobile || isNotScrolled;

    const showSearchIcon = !showSearchInput && isScrolled;

    return (
        <nav
            className={`order-10 flex w-full flex-col xs:gap-4 xs:p-4 sm:gap-6 sm:px-8 lg:px-10 xl:px-16 ${
                isScrolled ? 'border-b border-slt-pepper-200' : ''
            }`}
        >
            <Flex
                data-testid="header.menu"
                alignItems="center"
                width="100%"
                wrap="wrap"
                rowGap={4}
                columnGap={{ base: 2, md: 4 }}
            >
                <div className="order-1 m-1 flex md:hidden" data-testid="header.menu.hamburger">
                    <HamburgerIcon margin={1} onClick={onOpen} />
                </div>
                <HeaderLogoLink className="order-5 mr-4 flex grow justify-start md:grow-0" />

                {showSearchInput && (
                    <Flex
                        data-testid="header.menu.search"
                        width={{ base: '100%', md: 'auto' }}
                        grow={1}
                        order={{ xs: 35, md: 10 }}
                    >
                        <MenuSearch headerRef={headerRef} />
                    </Flex>
                )}

                {showSearchIcon && (
                    <Flex order={13} justifyContent="flex-end">
                        <SearchIcon
                            data-testid="header.menu.search.scroll.icon"
                            margin={1}
                            cursor="pointer"
                            onClick={(): void => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        />
                    </Flex>
                )}

                <div className="order-15 m-1 hidden items-start md:flex">
                    <MenuStore />
                </div>

                <Box margin={1} className="order-20">
                    <MenuCustomer />
                </Box>

                <SoftLink
                    to={cartUrl}
                    data-testid="header.menu.bag"
                    aria-label="Bag Link"
                    className="order-30"
                >
                    <Flex position="relative" margin={1}>
                        <BasketIcon data-testid="header.menu.bag.icon" margin={1} />
                        {basketCount ? (
                            <Flex
                                width={5}
                                height={5}
                                top={-1}
                                right={-1}
                                borderWidth={1}
                                borderRadius={32}
                                borderColor="slt-red-error"
                                backgroundColor="slt-red-error"
                                position="absolute"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Text id="header.menu.bag.label" size="xs" color="white">
                                    {basketCount}
                                </Text>
                            </Flex>
                        ) : null}
                    </Flex>
                </SoftLink>

                <Flex
                    data-testid="header.menu.category"
                    wrap="wrap"
                    gap={8}
                    grow={1}
                    width={showSearchInput ? '100%' : undefined}
                    order={showSearchInput ? 40 : 10}
                    className={`xs:!hidden md:!flex ${styles.popover}`}
                >
                    <MenuCategory isOpen={isOpen} onClose={onClose} />
                </Flex>
            </Flex>
        </nav>
    );
};
